import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { HiTrash, HiPencil } from 'react-icons/hi';
import { useGeneral } from '../../../contexts';
import { OrganizerPanelOutletContext } from '../../../types/organizerPanel.types';
import { Announcement, AnnouncementFormData } from '../../../types/service.types';
import Services from '../../../services/Services';
import { convertFileToBase64 } from '../../../utils/fileConversion';
import Button from '../../../components/ui/Button/Button';
import FormInput from '../../../components/ui/Form/FormInput';
import FormTextarea from '../../../components/ui/Form/FormTextarea';
import LoadingSkeleton from '../../../components/ui/LoadingSkeleton/LoadingSkeleton';
import Modal from '../../../components/ui/Modal/Modal';
import ImageUploadModal from '../../../components/ui/ImageUploadModal/ImageUploadModal';
import { Table, TableHeader, TableBody, TableRow, TableCell, EmptyTableRow } from '../../../components/ui/Table';
import styles from './MailingProcessPage.module.css';

const COLUMN_WIDTHS = ['1fr', '100px', '140px', '120px', '100px'];

const emptyFormData: AnnouncementFormData = {
  title: '',
  description: '',
  type: '',
  subTitle: '',
  image: null,
  imagePreview: null,
};

const formatDate = (timestamp: string): string => {
  const date = new Date(Number(timestamp) * 1000);
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const getCreatorName = (createdBy: Announcement['createdBy']): string => {
  if (Array.isArray(createdBy)) {
    const creator = createdBy[0];
    return creator ? `${creator.name} ${creator.lastname}`.trim() : '';
  }
  return `${createdBy.name} ${createdBy.lastname}`.trim();
};

const MailingProcessPage: React.FC = () => {
  const { channel } = useOutletContext<OrganizerPanelOutletContext>();
  const { auth, language, toastSuccess, toastError } = useGeneral();
  const { token } = auth;
  const { getTranslation } = language;

  // Data state
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState<Announcement | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Unsaved changes tracking - which modal triggered unsaved
  const pendingCloseRef = useRef<'create' | 'edit' | null>(null);

  // Form state
  const [formData, setFormData] = useState<AnnouncementFormData>({ ...emptyFormData });
  const initialFormDataRef = useRef<AnnouncementFormData | null>(null);

  // Fetch announcements
  const fetchAnnouncements = useCallback(async () => {
    if (!token || !channel?.channelID) return;
    setLoading(true);
    try {
      const response = await Services.SocialHiveGetAnnouncements(token, channel.channelID);
      if (response.data.status && response.data.result) {
        const sorted = (response.data.result as Announcement[]).sort(
          (a, b) => Number(b.createdDateTime || 0) - Number(a.createdDateTime || 0)
        );
        setAnnouncements(sorted);
      }
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, [token, channel?.channelID]);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  // Reset form when opening create modal
  useEffect(() => {
    if (showCreateModal) {
      const initial = { ...emptyFormData };
      setFormData(initial);
      initialFormDataRef.current = initial;
    }
  }, [showCreateModal]);

  // Populate form when opening edit modal
  useEffect(() => {
    if (showEditModal && selectedAnnouncement) {
      const initial: AnnouncementFormData = {
        title: selectedAnnouncement.title,
        description: selectedAnnouncement.description,
        type: selectedAnnouncement.type,
        subTitle: selectedAnnouncement.subTitle,
        image: null,
        imagePreview: selectedAnnouncement.image || null,
      };
      setFormData(initial);
      initialFormDataRef.current = initial;
    }
  }, [showEditModal, selectedAnnouncement]);

  // Check for unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    if ((!showCreateModal && !showEditModal) || !initialFormDataRef.current) return false;
    const initial = initialFormDataRef.current;
    return (
      formData.title !== initial.title ||
      formData.description !== initial.description ||
      formData.type !== initial.type ||
      formData.subTitle !== initial.subTitle ||
      formData.image !== initial.image ||
      formData.imagePreview !== initial.imagePreview
    );
  }, [showCreateModal, showEditModal, formData]);

  // Close modal with unsaved changes check
  const handleCloseModal = (modalType: 'create' | 'edit') => {
    if (hasUnsavedChanges) {
      pendingCloseRef.current = modalType;
      setShowUnsavedModal(true);
    } else {
      closeModal(modalType);
    }
  };

  const closeModal = (modalType: 'create' | 'edit') => {
    if (modalType === 'create') {
      setShowCreateModal(false);
    } else {
      setShowEditModal(false);
      setSelectedAnnouncement(null);
    }
    setFormData({ ...emptyFormData });
    initialFormDataRef.current = null;
  };

  const handleDiscardChanges = () => {
    setShowUnsavedModal(false);
    if (pendingCloseRef.current) {
      closeModal(pendingCloseRef.current);
      pendingCloseRef.current = null;
    }
  };

  const handleSaveAndContinue = async () => {
    setShowUnsavedModal(false);
    if (pendingCloseRef.current === 'create') {
      await handleCreate();
    } else if (pendingCloseRef.current === 'edit') {
      await handleUpdate();
    }
    pendingCloseRef.current = null;
  };

  // Image upload handler
  const handleImageUpload = async (file: File) => {
    const coverFile = await convertFileToBase64(file);
    const preview = URL.createObjectURL(file);
    setFormData(prev => ({ ...prev, image: coverFile, imagePreview: preview }));
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({ ...prev, image: null, imagePreview: null }));
  };

  // CRUD handlers
  const handleCreate = async () => {
    if (!token || !channel?.channelID) return;
    if (!formData.title.trim()) {
      toastError(getTranslation('organizerPanel.announcementTitleRequired'));
      return;
    }
    if (!formData.description.trim()) {
      toastError(getTranslation('organizerPanel.announcementDescriptionRequired'));
      return;
    }
    if (!formData.type.trim()) {
      toastError(getTranslation('organizerPanel.announcementTypeRequired'));
      return;
    }

    setSaving(true);
    try {
      const response = await Services.SocialHiveCreateAnnouncement(
        token,
        channel.channelID,
        formData.title.trim(),
        formData.description.trim(),
        formData.type.trim(),
        formData.subTitle.trim(),
        formData.image,
        1
      );
      if (response.data.status) {
        toastSuccess(getTranslation('organizerPanel.announcementCreated'));
        closeModal('create');
        fetchAnnouncements();
      } else {
        toastError(getTranslation('organizerPanel.announcementCreateError'));
      }
    } catch {
      toastError(getTranslation('organizerPanel.announcementCreateError'));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!token || !selectedAnnouncement) return;
    if (!formData.title.trim()) {
      toastError(getTranslation('organizerPanel.announcementTitleRequired'));
      return;
    }
    if (!formData.description.trim()) {
      toastError(getTranslation('organizerPanel.announcementDescriptionRequired'));
      return;
    }
    if (!formData.type.trim()) {
      toastError(getTranslation('organizerPanel.announcementTypeRequired'));
      return;
    }

    setSaving(true);
    try {
      const response = await Services.SocialHiveUpdateAnnouncement(
        token,
        selectedAnnouncement.announcementID,
        formData.title.trim(),
        formData.description.trim(),
        formData.type.trim(),
        formData.subTitle.trim(),
        formData.image,
        1
      );
      if (response.data.status) {
        toastSuccess(getTranslation('organizerPanel.announcementUpdated'));
        closeModal('edit');
        fetchAnnouncements();
      } else {
        toastError(getTranslation('organizerPanel.announcementUpdateError'));
      }
    } catch {
      toastError(getTranslation('organizerPanel.announcementUpdateError'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!token || !selectedAnnouncement) return;
    setDeleting(true);
    try {
      const response = await Services.SocialHiveRemoveAnnouncement(
        token,
        selectedAnnouncement.announcementID
      );
      if (response.data.status) {
        toastSuccess(getTranslation('organizerPanel.announcementDeleted'));
        setShowDeleteModal(false);
        setSelectedAnnouncement(null);
        fetchAnnouncements();
      } else {
        toastError(getTranslation('organizerPanel.announcementDeleteError'));
      }
    } catch {
      toastError(getTranslation('organizerPanel.announcementDeleteError'));
    } finally {
      setDeleting(false);
    }
  };

  // Open edit/delete
  const handleOpenEdit = (announcement: Announcement) => {
    setSelectedAnnouncement(announcement);
    setShowEditModal(true);
  };

  const handleOpenDelete = (announcement: Announcement) => {
    setSelectedAnnouncement(announcement);
    setShowDeleteModal(true);
  };

  // Render form fields (shared between create and edit modals)
  const renderFormFields = () => (
    <div className={styles.modalForm}>
      <div className={styles.formGroup}>
        <label className={styles.formLabel}>
          {getTranslation('organizerPanel.announcementTitle')} *
        </label>
        <FormInput
          value={formData.title}
          onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
          placeholder={getTranslation('organizerPanel.eventTitlePlaceholder')}
          maxLength={100}
          disabled={saving}
        />
      </div>

      <div className={styles.formGroup}>
        <label className={styles.formLabel}>
          {getTranslation('organizerPanel.announcementSubTitle')}{' '}
          <span className={styles.formLabelOptional}>({getTranslation('events.optional')})</span>
        </label>
        <FormInput
          value={formData.subTitle}
          onChange={(e) => setFormData(prev => ({ ...prev, subTitle: e.target.value }))}
          placeholder={getTranslation('organizerPanel.announcementSubTitlePlaceholder')}
          maxLength={150}
          disabled={saving}
        />
      </div>

      <div className={styles.formGroup}>
        <label className={styles.formLabel}>
          {getTranslation('organizerPanel.announcementType')} *
        </label>
        <FormInput
          value={formData.type}
          onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
          placeholder={getTranslation('organizerPanel.announcementTypePlaceholder')}
          disabled={saving}
        />
      </div>

      <div className={styles.formGroup}>
        <label className={styles.formLabel}>
          {getTranslation('organizerPanel.announcementDescription')} *
        </label>
        <FormTextarea
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          placeholder={getTranslation('organizerPanel.eventDescriptionPlaceholder')}
          maxLength={1000}
          rows={4}
          disabled={saving}
        />
      </div>

      <div className={styles.formGroup}>
        <label className={styles.formLabel}>
          {getTranslation('organizerPanel.announcementImage')}{' '}
          <span className={styles.formLabelOptional}>({getTranslation('events.optional')})</span>
        </label>
        <div className={styles.imageUploadArea}>
          {formData.imagePreview ? (
            <div className={styles.imagePreviewContainer}>
              <img
                src={formData.imagePreview}
                alt="Preview"
                className={styles.imagePreview}
              />
            </div>
          ) : null}
          <div className={styles.imageActions}>
            <Button
              variant="secondary"
              size="small"
              onClick={() => setShowImageModal(true)}
              disabled={saving}
            >
              {formData.imagePreview
                ? getTranslation('createChannel.changeImage')
                : getTranslation('imageUpload.upload')
              }
            </Button>
            {formData.imagePreview && (
              <Button
                variant="secondary"
                size="small"
                onClick={handleRemoveImage}
                disabled={saving}
              >
                {getTranslation('createChannel.removeImage')}
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className={styles.modalActions}>
        <Button
          variant="secondary"
          size="medium"
          onClick={() => handleCloseModal(showCreateModal ? 'create' : 'edit')}
          disabled={saving}
        >
          {getTranslation('common.cancel')}
        </Button>
        <Button
          variant="primary"
          size="medium"
          onClick={showCreateModal ? handleCreate : handleUpdate}
          disabled={saving || !formData.title.trim() || !formData.description.trim() || !formData.type.trim()}
        >
          {saving
            ? getTranslation('common.saving')
            : getTranslation('common.save')
          }
        </Button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className={styles.announcementsPage}>
        <div className={styles.pageHeader}>
          <LoadingSkeleton height={28} width={200} variant="text" />
          <LoadingSkeleton height={42} width={180} />
        </div>
        <div className={styles.skeletonTable}>
          <div className={styles.skeletonHeader}>
            <LoadingSkeleton height={14} width={120} variant="text" />
            <LoadingSkeleton height={14} width={80} variant="text" />
            <LoadingSkeleton height={14} width={100} variant="text" />
          </div>
          {[1, 2, 3].map((i) => (
            <div key={i} className={styles.skeletonRow}>
              <LoadingSkeleton height={20} width={180} variant="text" />
              <LoadingSkeleton height={20} width={60} variant="text" />
              <LoadingSkeleton height={20} width={100} variant="text" />
              <LoadingSkeleton height={20} width={80} variant="text" />
              <LoadingSkeleton height={32} width={72} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.announcementsPage}>
      <div className={styles.pageHeader}>
        <h2 className={styles.pageTitle}>
          {getTranslation('organizerPanel.announcements')}
        </h2>
        <Button
          variant="primary"
          size="medium"
          onClick={() => setShowCreateModal(true)}
        >
          {getTranslation('organizerPanel.createAnnouncement')}
        </Button>
      </div>

      <Table>
        <TableHeader
          columns={[
            getTranslation('organizerPanel.announcementTitle'),
            getTranslation('organizerPanel.announcementType'),
            getTranslation('organizerPanel.announcementCreatedBy'),
            getTranslation('organizerPanel.announcementDate'),
            '',
          ]}
          columnWidths={COLUMN_WIDTHS}
        />
        <TableBody>
          {announcements.length === 0 ? (
            <EmptyTableRow
              message={getTranslation('organizerPanel.noAnnouncements')}
            />
          ) : (
            announcements.map((announcement) => (
              <TableRow
                key={announcement.announcementID}
                columnWidths={COLUMN_WIDTHS}
              >
                <TableCell>
                  <span className={styles.announcementTitle}>
                    {announcement.title}
                  </span>
                </TableCell>
                <TableCell>
                  <span className={styles.announcementType}>
                    {announcement.type}
                  </span>
                </TableCell>
                <TableCell>
                  <span className={styles.createdByName}>
                    {getCreatorName(announcement.createdBy)}
                  </span>
                </TableCell>
                <TableCell>
                  <span className={styles.dateText}>
                    {formatDate(announcement.createdDateTime)}
                  </span>
                </TableCell>
                <TableCell align="right">
                  <div className={styles.actionButtons}>
                    <button
                      type="button"
                      className={styles.editButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenEdit(announcement);
                      }}
                      aria-label="Edit"
                    >
                      {/* @ts-ignore - react-icons compatibility with React 19 */}
                      <HiPencil size={16} />
                    </button>
                    <button
                      type="button"
                      className={styles.deleteButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenDelete(announcement);
                      }}
                      aria-label="Delete"
                    >
                      {/* @ts-ignore - react-icons compatibility with React 19 */}
                      <HiTrash size={16} />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/* Create Announcement Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => handleCloseModal('create')}
        title={getTranslation('organizerPanel.createAnnouncement')}
        variant="default"
      >
        {renderFormFields()}
      </Modal>

      {/* Edit Announcement Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => handleCloseModal('edit')}
        title={getTranslation('organizerPanel.editAnnouncement')}
        variant="default"
      >
        {renderFormFields()}
      </Modal>

      {/* Delete Confirm Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedAnnouncement(null);
        }}
        title={getTranslation('organizerPanel.deleteAnnouncement')}
        variant="confirm"
        message={getTranslation('organizerPanel.deleteAnnouncementConfirm')}
        confirmText={getTranslation('common.delete')}
        cancelText={getTranslation('common.cancel')}
        onConfirm={handleDelete}
        onCancel={() => {
          setShowDeleteModal(false);
          setSelectedAnnouncement(null);
        }}
        isDanger
        disabled={deleting}
      />

      {/* Unsaved Changes Modal */}
      <Modal
        isOpen={showUnsavedModal}
        onClose={() => setShowUnsavedModal(false)}
        title={getTranslation('organizerPanel.unsavedChanges')}
        variant="confirm"
        message={getTranslation('organizerPanel.unsavedChangesMessage')}
        confirmText={getTranslation('organizerPanel.saveAndContinue')}
        cancelText={getTranslation('organizerPanel.discardChanges')}
        onConfirm={handleSaveAndContinue}
        onCancel={handleDiscardChanges}
      />

      {/* Image Upload Modal */}
      <ImageUploadModal
        isOpen={showImageModal}
        onClose={() => setShowImageModal(false)}
        onUpload={handleImageUpload}
        title={getTranslation('organizerPanel.announcementImage')}
        aspectRatio="16:9"
        recommendedSize="1920x1080"
        getTranslation={getTranslation}
      />
    </div>
  );
};

export default MailingProcessPage;
